import { Link } from '@tanstack/react-router'
import { AlertTriangle, Landmark } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency } from '@/lib/format'
import { useFloorplanSummary } from '@/features/finance/hooks'
import { CURTAILMENT_FREQUENCY_LABELS } from '@/types/inventory'

const formatDate = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

const todayISO = () => new Date().toISOString().slice(0, 10)

// Floor-plan tab on the finance page: aggregate exposure across active loans
// plus one row per loan with its accrued unpaid curtailments and next due
// date. Payments are marked from each boat's page — the loans live with the
// inventory they finance.
export function FloorplanSummary() {
  const { data: summary, isLoading } = useFloorplanSummary()

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!summary || summary.loans.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-4 w-4" /> Floor Plan
          </CardTitle>
          <CardDescription>
            No floor-plan loans on file. Add one from a boat's inventory page to track its
            curtailment and interest payments here.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const interestUnpaid = summary.loans.reduce((sum, l) => sum + l.interest_unpaid_total, 0)

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Loans</CardTitle>
            <Landmark className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.active_loans}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summary.total_principal)} financed
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accrued Unpaid</CardTitle>
            <AlertTriangle
              className={`h-4 w-4 ${summary.accrued_unpaid_count > 0 ? 'text-warning' : 'text-muted-foreground'}`}
            />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary.accrued_unpaid_total)}</div>
            <p className="text-xs text-muted-foreground">
              {summary.accrued_unpaid_count > 0
                ? `${summary.accrued_unpaid_count} curtailment${summary.accrued_unpaid_count === 1 ? '' : 's'} due`
                : 'all current'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Interest Accrued</CardTitle>
            <AlertTriangle
              className={`h-4 w-4 ${interestUnpaid > 0 ? 'text-warning' : 'text-muted-foreground'}`}
            />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(interestUnpaid)}</div>
            <p className="text-xs text-muted-foreground">unpaid interest charges</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.paid_this_month + summary.interest_paid_this_month)}
            </div>
            <p className="text-xs text-muted-foreground">
              curtailments + {formatCurrency(summary.interest_paid_this_month)} interest
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Interest Paid YTD</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.interest_paid_year_to_date)}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summary.interest_paid_this_month)} this month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Financing Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary.total_financing_cost)}</div>
            <p className="text-xs text-muted-foreground">
              all curtailments + interest paid on active loans
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Loans</CardTitle>
          <CardDescription>
            Curtailment and interest payments are marked from each boat's inventory page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Boat</TableHead>
                  <TableHead>Lender</TableHead>
                  <TableHead className="text-right">Principal</TableHead>
                  <TableHead className="text-right">Curtailment</TableHead>
                  <TableHead className="text-right">APR</TableHead>
                  <TableHead className="text-right">Accrued unpaid</TableHead>
                  <TableHead className="text-right">Interest paid</TableHead>
                  <TableHead>Next due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.loans.map((l) => {
                  const overdue =
                    l.next_due_on && l.next_due_on.slice(0, 10) < todayISO()
                  return (
                    <TableRow key={l.loan_id}>
                      <TableCell className="font-medium">
                        <Link
                          to="/inventory/$boatId"
                          params={{ boatId: l.boat_id }}
                          className="underline-offset-4 hover:underline"
                        >
                          {l.boat_label || 'Boat'}
                        </Link>
                      </TableCell>
                      <TableCell>{l.lender}</TableCell>
                      <TableCell className="text-right">{formatCurrency(l.principal)}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(l.curtailment_amount)} ·{' '}
                        {CURTAILMENT_FREQUENCY_LABELS[l.frequency].toLowerCase()}
                      </TableCell>
                      <TableCell className="text-right">
                        {l.interest_rate > 0 ? `${l.interest_rate}%` : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {l.accrued_unpaid_count > 0 ? (
                          <span className="font-medium text-destructive">
                            {formatCurrency(l.accrued_unpaid_total)}
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({l.accrued_unpaid_count})
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {l.interest_paid_total > 0
                          ? formatCurrency(l.interest_paid_total)
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {l.next_due_on ? (
                          <Badge variant={overdue ? 'destructive' : 'outline'}>
                            {formatDate(l.next_due_on)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
